import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Calendar } from '@design-system/Calendar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useGetPauseReasonOptions,
  useGetPauseResumeDueDate,
  useCalculatePauseDatesMutation,
  usePauseSubscription,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type { PauseReasonOption } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  formatSMPDate,
  formatDateWithDashes,
} from './utils/smpHelpers';
import { useUIMessages } from './hooks/useUIMessages';
import { SMPDialogContent, SMPMutationErrorDialog, OrderInProgressDisclaimer } from './components';
import { SMP_PATH } from '@/constants/paths';
import { logSMPMutationError } from '@/utils/sentryLogger';

type Step = 'order-in-progress' | 'reason-selection' | 'retention' | 'calendar';

const SCREEN_ID_KEEP_OR_CONTINUE = 'KEEP_SUBSCRIPTION_OR_CONTINUE';

// ============================================
// Radio Option Card
// ============================================

const RadioOptionCard = ({
  label,
  selected,
  onSelect,
  testId,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`
      flex items-center justify-between w-full p-4 rounded-xl border-2 transition-colors text-left
      ${selected ? 'border-success bg-white' : 'border-stroke-secondary-subtle bg-white'}
    `}
    data-testid={testId}
  >
    <span className="typography-body text-primary">{label}</span>
    <div
      className={`
        size-5 rounded-full border-2 flex items-center justify-center shrink-0
        ${selected ? 'border-success' : 'border-stroke-secondary-subtle'}
      `}
    >
      {selected && <div className="size-2.5 rounded-full bg-success" />}
    </div>
  </button>
);

// ============================================
// Shuffle helper
// ============================================

const shuffleArray = <T,>(arr: T[]): T[] => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ============================================
// Main Page
// ============================================

export const SMPPauseSubscriptionPage = () => {
  const { smpNavigate, smpGoBack } = useSMPNavigate();
  const location = useLocation();
  const cancellationFlowId = (location.state as { cancellationFlowId?: number } | null)?.cancellationFlowId;
  const {
    subscriptionId,
    subscriptionDetail,
    hasOrderInProgress,
    uiMessages,
    showMaintenance,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const { getMessage, getMessageText } = useUIMessages(uiMessages);

  const [step, setStep] = useState<Step | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [selectedReason, setSelectedReason] = useState<PauseReasonOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [successData, setSuccessData] = useState<{
    billingDate: string;
    deliveryDate: string;
  } | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const pauseSubscriptionMutation = usePauseSubscription();
  const calculateDatesMutation = useCalculatePauseDatesMutation();

  const productSku = subscriptionDetail?.line_items?.[0]?.sku ?? '';

  const {
    data: reasonOptions,
    isLoading: isReasonsLoading,
  } = useGetPauseReasonOptions({
    enabled: isSessionReady,
  });

  const {
    data: dueDateData,
    isLoading: isDueDateLoading,
  } = useGetPauseResumeDueDate(productSku, subscriptionId, {
    enabled: isSessionReady && !!subscriptionId && !!productSku,
  });

  const pauseReasons = useMemo(() => {
    if (!reasonOptions) return [];
    const filtered = reasonOptions.filter((r) => r.flowType === 'PAUSE');
    const shuffled = shuffleArray(filtered);
    shuffled.push({
      id: filtered.length + 1,
      label: 'Other',
      flowType: 'PAUSE',
      screenId: SCREEN_ID_KEEP_OR_CONTINUE,
      educationContentUIMessage: 'pause_reason_other',
    });
    return shuffled;
  }, [reasonOptions]);

  const minDate = useMemo(
    () => (dueDateData?.minDueDate ? new Date(dueDateData.minDueDate) : new Date()),
    [dueDateData],
  );
  const maxDate = useMemo(
    () => (dueDateData?.maxDueDate ? new Date(dueDateData.maxDueDate) : new Date()),
    [dueDateData],
  );

  // Opened from the cancel-retention flow (cancellationFlowId set): a history
  // back would remount the cancel page instead of returning to the SMP hub.
  const goBack = useCallback(
    () => (cancellationFlowId ? smpNavigate(SMP_PATH, { replace: true }) : smpGoBack()),
    [cancellationFlowId, smpNavigate, smpGoBack],
  );
  const isLoading = isSMPLoading || isReasonsLoading || isDueDateLoading;

  useEffect(() => {
    if (isLoading || initialized || !subscriptionDetail) return;
    setInitialized(true);
    setStep(hasOrderInProgress ? 'order-in-progress' : 'reason-selection');
  }, [isLoading, initialized, subscriptionDetail, hasOrderInProgress]);

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  if (subscriptionDetail.subscription_status !== 'active' && !successData) {
    smpNavigate(SMP_PATH, { replace: true });
    return null;
  }

  const handleReasonContinue = () => {
    if (!selectedReason) return;
    setStep('retention');
  };

  const handleKeepSubscription = () => {
    goBack();
  };

  const handleContinueToPause = () => {
    setStep('calendar');
    setSelectedDate(minDate);
    setCalendarMonth(minDate);
  };

  const handleCalendarConfirm = () => {
    calculateDatesMutation.mutate(
      {
        subscriptionId,
        deliveryDate: formatDateWithDashes(selectedDate),
      },
      {
        onSuccess: (response) => {
          const nextOrderDate = response.next_order_date;
          const estimatedDeliveryDate = response.estimated_delivery_date;

          pauseSubscriptionMutation.mutate(
            {
              subscriptionId,
              nextOrderDate,
              estimatedDeliveryDate,
              reason: selectedReason?.label,
              ...(cancellationFlowId ? { cancellationFlowId } : {}),
            },
            {
              onSuccess: (data: unknown) => {
                const res = data as {
                  data?: {
                    nextOrderDate?: string;
                    nextDeliveryDate?: string;
                  };
                  nextOrderDate?: string;
                  nextDeliveryDate?: string;
                };
                const billing = res?.data?.nextOrderDate || res?.nextOrderDate || nextOrderDate;
                const delivery = res?.data?.nextDeliveryDate || res?.nextDeliveryDate || estimatedDeliveryDate;
                setSuccessData({
                  billingDate: formatSMPDate(billing),
                  deliveryDate: formatSMPDate(delivery),
                });
              },
              onError: (error) => {
                setShowErrorDialog(true);
                logSMPMutationError('pauseSubscription', error, {
                  screen: 'SMPPauseSubscriptionPage',
                  subscriptionId,
                });
              },
            },
          );
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('calculatePauseDates', error, {
            screen: 'SMPPauseSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleSuccessClose = () => {
    setSuccessData(null);
    smpNavigate(SMP_PATH, { replace: true });
  };

  const isSubmitting =
    pauseSubscriptionMutation.isPending ||
    calculateDatesMutation.isPending;

  const handleNavBack = () => {
    switch (step) {
      case 'retention':
        setStep('reason-selection');
        break;
      case 'calendar':
        setStep('retention');
        break;
      default:
        goBack();
    }
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-pausePage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={handleNavBack}
        />
      </div>

      {/* Order in Progress */}
      {step === 'order-in-progress' && subscriptionDetail.lacore_order && (
        <OrderInProgressDisclaimer
          lacoreOrder={subscriptionDetail.lacore_order}
          planName={subscriptionDetail.line_items?.[0]?.product_name}
          productImage={subscriptionDetail.line_items?.[0]?.image}
          onContinue={() => setStep('reason-selection')}
          onCancel={goBack}
          continueLabel="Proceed to Pause"
        />
      )}

      {/* Step 1: Reason Selection */}
      {step === 'reason-selection' && (
        <div className="flex flex-col gap-8" data-testid="smp-pause-reasonStep">
          <h2
            className="typography-headline2 text-primary"
            data-testid="smp-pause-reasonTitle"
          >
            Please tell us the main reason why you want to pause your
            subscription.
          </h2>

          <div className="flex flex-col gap-3 w-full max-w-sm">
            {pauseReasons.map((reason) => (
              <RadioOptionCard
                key={reason.id}
                label={reason.label}
                selected={selectedReason?.id === reason.id}
                onSelect={() => setSelectedReason(reason)}
                testId={`smp-pause-reason-${reason.id}`}
              />
            ))}
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleReasonContinue}
              disabled={!selectedReason}
              data-testid="smp-pause-reasonContinue"
            >
              Continue
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              data-testid="smp-pause-reasonCancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Retention / Education */}
      {step === 'retention' && selectedReason && (
        <div className="flex flex-col gap-8" data-testid="smp-pause-retentionStep">
          {selectedReason.educationContentUIMessage && (() => {
            const educationMsg = getMessage(selectedReason.educationContentUIMessage);
            const htmlContent = educationMsg?.bodyHtml;
            if (htmlContent) {
              return (
                <div
                  className="typography-body text-secondary [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-3"
                  data-testid="smp-pause-retentionContent"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              );
            }
            return null;
          })()}

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleKeepSubscription}
              data-testid="smp-pause-keepActive"
            >
              Keep subscription active
            </Button>
            <Button
              variant="secondary"
              onClick={handleContinueToPause}
              data-testid="smp-pause-continuePause"
            >
              Continue to pause subscription
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Calendar Date Picker */}
      {step === 'calendar' && (
        <div className="flex flex-col gap-8" data-testid="smp-pause-calendarStep">
          <div className="flex flex-col gap-4">
            <h2
              className="typography-h2 text-primary"
              data-testid="smp-pause-calendarTitle"
            >
              Using the calendar below, select the date you&apos;d like to
              receive your next order.
            </h2>
            <p className="typography-body text-secondary">
              Your subscription will resume and you will be billed 2 weeks
              before the delivery date selected.
            </p>
          </div>

          <div className="[&>div]:items-start max-w-full overflow-x-auto">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setCalendarMonth(date);
                }
              }}
              fromDate={minDate}
              toDate={maxDate}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
            />
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleCalendarConfirm}
              disabled={isSubmitting}
              data-testid="smp-pause-calendarConfirm"
            >
              {isSubmitting ? 'Processing...' : 'Pause until this date'}
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={isSubmitting}
              data-testid="smp-pause-calendarCancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      <Dialog open={!!successData} onOpenChange={(isOpen) => !isOpen && handleSuccessClose()}>
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">
              Subscription Paused
            </DialogTitle>
            <DialogDescription asChild>
              {(() => {
                const pauseAlertHtml = getMessageText(
                  'smp_pause_submit_alert',
                  'bodyHtml',
                  {
                    deliveryDate: successData?.deliveryDate ?? '',
                    billedDate: successData?.billingDate ?? '',
                  },
                );
                if (pauseAlertHtml) {
                  return (
                    <div
                      className="typography-body text-secondary [&_p]:mb-3"
                      dangerouslySetInnerHTML={{ __html: pauseAlertHtml }}
                    />
                  );
                }
                return (
                  <div className="flex flex-col gap-4">
                    <p className="typography-body text-secondary">
                      Your subscription has been paused temporarily and the
                      estimated delivery date of your next order is{' '}
                      {successData?.deliveryDate}.
                    </p>
                    <p className="typography-body text-secondary">
                      Your subscription will resume and you will be billed on{' '}
                      {successData?.billingDate}. You will receive a reminder
                      notification 2 days prior to that date. You may also resume
                      your subscription at any time.
                    </p>
                  </div>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while pausing your subscription. Please try again."
      />
    </section>
  );
};
